<template>
  <AppLayout
    centered
    v-if="booking && !isLoadingBooking"
    parent="/jobs"
    isDetails
    :title="booking.title"
    class="tabs-container"
    data-test="bookingContent"
  >
    <template v-slot:header>
      <div class="booking-details-header overflow-auto">
        <Chip :status="booking.status">
          {{ booking.status }}
        </Chip>
        <div class="entity-id">R-{{ currentBookingId }}</div>
      </div>
    </template>
    <template v-slot:body>
      <div class="subheader d-flex flex-column">
        <div class="d-flex justify-center mb-2">
          <!--
            <PrimaryButton
              text
              @click.native="onFileDownload"
              :loading="isDownloadingFile"
              class="narrow-button"
            >
              Download as a PDF
              <template v-slot:prepend:icon>
                <v-icon color="primary" class="mr-2">
                  mdi-download
                </v-icon>
              </template>
          -->
          <!-- </PrimaryButton> -->
          <ApplyLink v-if="showApplyLink" :booking="booking" />
          <ShareBookingAction v-if="showShareAction" />
        </div>
        <v-tabs
          centered
          v-model="selectedTab"
          class="basic-tabs"
          id="booking-tabs"
          @change="handleTabChange"
          v-if="showTabs"
        >
          <v-tab
            v-for="section in bookingSections"
            :key="section"
            data-test="bookingTab"
          >
            {{ section | capitalize }}
          </v-tab>
        </v-tabs>
      </div>
      <div class="container booking-tab-view col-lg-10 col-sm-12 pt-0">
        <router-view
          v-if="booking"
          :currentBooking="booking"
          :submittedWorkerIds="submittedWorkerIds"
        />
      </div>
    </template>
  </AppLayout>
  <v-overlay v-else z-index="10" data-test="loader">
    <v-progress-circular indeterminate size="64" />
  </v-overlay>
</template>

<script>
import AppLayout from "@/components/common/base-layouts/AppLayout";
import ShareBookingAction from "@/views/bookings/components/actions/ShareBookingAction";
import ApplyLink from "@/views/bookings/components/ApplyLink";
import {
  GET_CURRENT_BOOKING,
  IS_LOADING_BOOKING
} from "@/store/modules/bookings/getters";
import {
  BOOKINGS_NAMESPACE,
  FETCH_CURRENT_BOOKING
} from "@/store/modules/bookings/actions";
import { createNamespacedHelpers } from "vuex";
import Chip from "@/components/common/Chip";
import { CLEAR_BOOKING_DETAILS } from "@/store/modules/bookings/mutations";
import { SET_MODULE_PERMISSIONS_AS_CURRENT } from "@/store/modules/auth/mutation-types";
import { AUTH_NAMESPACE } from "@/store/modules/auth";
import { MAIN_NAVIGATION_ITEMS } from "@/router/constants";
import { includes, size } from "lodash";
import { fileToDownload } from "@/utils/helpers";
import { isPermissioned } from "@/utils/permissions";
import { POST_GLOBAL_MESSAGE } from "@/store/modules/global/action-types";
import { mapActions } from "vuex";
import $ from "jquery";

const {
  mapActions: bookingsActions,
  mapGetters,
  mapMutations
} = createNamespacedHelpers(BOOKINGS_NAMESPACE);
const { mapMutations: mapAuthMutations, mapState } = createNamespacedHelpers(
  AUTH_NAMESPACE
);
const { JOBS } = MAIN_NAVIGATION_ITEMS;
const TAB_INDEXES = {
  DETAILS: 0,
  CANDIDATES: 1,
  INTERVIEWS: 2,
  OFFERS: 3,
  CONVERSATIONS: 4
};

export default {
  name: "BookingTabs",
  components: {
    Chip,
    AppLayout,
    ShareBookingAction,
    ApplyLink
  },
  created() {
    this.onInit();
  },
  data() {
    return {
      savedBookingId: null,
      selectedTab: 0,
      submittedWorkerIds: [],
      isDownloadingFile: false
    };
  },
  computed: {
    ...mapState({
      permissions: state => state.permissions
    }),
    ...mapGetters({
      booking: GET_CURRENT_BOOKING,
      isLoadingBooking: IS_LOADING_BOOKING
    }),
    showTabs() {
      return size(this.bookingSections) > 1;
    },
    currentBookingId() {
      return this.$route.params.id;
    },
    initialSection() {
      const path = this.$router.currentRoute.path;
      switch (true) {
        case includes(path, "offers"):
          return TAB_INDEXES.OFFERS;
        case includes(path, "candidates"):
          return TAB_INDEXES.CANDIDATES;
        case includes(path, "interviews"):
          return TAB_INDEXES.INTERVIEWS;
        case includes(path, "conversations"):
          return TAB_INDEXES.CONVERSATIONS;
        case includes(path, "details"):
        default:
          return TAB_INDEXES.DETAILS;
      }
    },
    bookingSections() {
      return [
        "details",
        this.showTab("applications") && "candidates",
        this.showTab("interviews") && "interviews",
        this.showTab("offers") && "offers",
        this.showTab("conversations") && "conversations"
      ].filter(Boolean);
    },
    showApplyLink() {
      return isPermissioned(
        `bookings[${this.currentBookingId}].view.publishing-schedule`
      );
    },
    showShareAction() {
      return isPermissioned(
        `bookings[${this.currentBookingId}].edit.shared-with`
      );
    }
  },
  methods: {
    ...mapActions({ postGlobalMessage: POST_GLOBAL_MESSAGE }),
    ...bookingsActions({
      fetchCurrentBooking: FETCH_CURRENT_BOOKING
    }),
    ...mapMutations({
      clearBookingDetails: CLEAR_BOOKING_DETAILS
    }),
    ...mapAuthMutations({
      setModulePermissionsAsCurrent: SET_MODULE_PERMISSIONS_AS_CURRENT
    }),
    async onInit() {
      this.setModulePermissionsAsCurrent({
        module: "bookings",
        id: this.currentBookingId
      });
      this.clearBookingDetails(this.currentBookingId);
      await this.fetchCurrentBooking(this.currentBookingId);
      if (!this.booking) {
        return this.$router.push("/pageNotFound");
      }
      document.title = `${this.booking.title} - Roles - Track`;
      this.selectedTab = this.initialSection;
    },
    showTab(permission) {
      return isPermissioned(
        `bookings[${this.currentBookingId}].view.${permission}`
      );
    },
    handleTabChange(selectedTab) {
      const tabName = this.bookingSections[selectedTab];
      if (tabName === "candidates" && !this.isMobile) {
        // disable global scrolling on canditates page
        const $applicationElement = $(".v-application");
        $applicationElement.css("overflow", "hidden");
      }
      this.$router.replace(`/${JOBS}/${this.currentBookingId}/${tabName}`);
    },
    async onFileDownload() {
      const file = {
        url: `${process.env.VUE_APP_API_URL}/api/v1/bookings/${this.currentBookingId}/download`,
        fileName: `job-${this.currentBookingId}.pdf`
      };
      this.isDownloadingFile = true;
      await fileToDownload(file);
      this.isDownloadingFile = false;
    },
    shouldPageBeReloaded(from, to) {
      const { id: prevBookingId } = from.params;
      const { id: currentBookingId } = to.params;

      return prevBookingId !== currentBookingId;
    }
  },
  provide() {
    return {
      currentBookingId: this.currentBookingId
    };
  },
  watch: {
    async $route(to, from) {
      if (this.shouldPageBeReloaded(to, from)) {
        this.onInit();
      }
    }
  }
};
</script>

<style lang="scss"></style>
